import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiService, graphBuilderApi } from '../../services/api';
import NodeNeighborhoodGraph from './NodeNeighborhoodGraph';
import './GraphEntityExplorer.css';

/**
 * GraphEntityExplorer - Comprehensive node/synapse navigation and editing interface
 * 
 * Features:
 * - View/edit node details
 * - View all connected synapses (incoming/outgoing)
 * - Navigate to synapses and connected nodes
 * - Back navigation with history stack
 * - Breadcrumb trail
 */

// Entity types
const ENTITY_TYPE = {
  NODE: 'node',
  SYNAPSE: 'synapse',
};

// Tab options for node view
const NODE_TABS = {
  DETAILS: 'details',
  OUTGOING: 'outgoing',
  INCOMING: 'incoming',
  NETWORK: 'network',
};

function GraphEntityExplorer({
  isOpen,
  initialEntity, // { type: 'node'|'synapse', id: uuid, data?: object }
  graphId,
  onClose,
  onEntityUpdated,
  onEditNode, // Callback to open node in full edit modal
  onEditSynapse, // Callback to open synapse in full edit modal
}) {
  // Navigation history stack
  const [history, setHistory] = useState([]);
  const [currentEntity, setCurrentEntity] = useState(null);
  
  // Entity data
  const [nodeData, setNodeData] = useState(null);
  const [synapseData, setSynapseData] = useState(null);
  const [connectedSynapses, setConnectedSynapses] = useState({ incoming: [], outgoing: [] });
  
  // UI state
  const [activeTab, setActiveTab] = useState(NODE_TABS.DETAILS);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  
  // Form state for node editing
  const [nodeForm, setNodeForm] = useState({
    name: '',
    type: '',
    description: '',
    attributesJson: '',
  });
  
  const [reindexStatus, setReindexStatus] = useState(null); // null | 'loading' | 'success' | 'error'
  const [processLinks, setProcessLinks] = useState([]);

  // Form state for synapse editing
  const [synapseForm, setSynapseForm] = useState({
    edgeName: '',
    description: '',
    weight: 1.0,
    delayMs: 0,
  });

  // Initialize with initial entity
  useEffect(() => {
    if (isOpen && initialEntity) {
      setHistory([]);
      setCurrentEntity(initialEntity);
      setActiveTab(NODE_TABS.DETAILS);
      setIsEditing(false);
    }
  }, [isOpen, initialEntity]);

  // Fetch process links for the current node
  useEffect(() => {
    if (!currentEntity || currentEntity.type !== ENTITY_TYPE.NODE || !currentEntity.id) {
      setProcessLinks([]);
      return;
    }
    graphBuilderApi.listGraphLinks({ knowledge_node_id: currentEntity.id })
      .then((resp) => setProcessLinks(resp?.links || []))
      .catch(() => setProcessLinks([]));
  }, [currentEntity]);

  // Load entity data when current entity changes
  const loadEntityData = useCallback(async () => {
    if (!currentEntity || !currentEntity.id) return;
    
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      if (currentEntity.type === ENTITY_TYPE.NODE) {
        // Load node details
        const response = await apiService.getNode(currentEntity.id, { include_related: true });
        if (response.success && response.node) {
          setNodeData(response.node);
          setSynapseData(null);
          
          // Initialize form
          setNodeForm({
            name: response.node.name || '',
            type: response.node.type || '',
            description: response.node.description || '',
            attributesJson: response.node.attributes 
              ? JSON.stringify(response.node.attributes, null, 2) 
              : '',
          });
          
          // Load connected synapses
          await loadConnectedSynapses(currentEntity.id);
        } else {
          throw new Error(response.message || 'Failed to load node');
        }
      } else if (currentEntity.type === ENTITY_TYPE.SYNAPSE) {
        // Load synapse details
        const response = await apiService.getSynapse(currentEntity.id);
        if (response.success && response.synapse) {
          setSynapseData(response.synapse);
          setNodeData(null);
          
          // Initialize form
          setSynapseForm({
            edgeName: response.synapse.edgeName || response.synapse.edgename || '',
            description: response.synapse.description || '',
            weight: response.synapse.weight || 1.0,
            delayMs: response.synapse.delayMs || response.synapse.delayms || 0,
          });
        } else {
          throw new Error(response.message || 'Failed to load synapse');
        }
      }
    } catch (err) {
      console.error('Error loading entity:', err);
      setError(err.message || 'Failed to load entity');
    } finally {
      setLoading(false);
    }
  }, [currentEntity]);

  // Load connected synapses for a node
  const loadConnectedSynapses = async (nodeId) => {
    if (!graphId) {
      console.warn('GraphEntityExplorer: graphId is not set, cannot load synapses');
      setConnectedSynapses({ incoming: [], outgoing: [] });
      return;
    }
    
    try {
      console.log('Loading synapses for node:', nodeId, 'in graph:', graphId);
      
      // Use searchSynapses with nodeId filter for server-side filtering
      // (works for all graph sizes, unlike listSynapses which returns empty for large graphs)
      const response = await apiService.searchSynapses({
        graphId,
        nodeId,
        pageSize: 500,
      });
      
      console.log('Synapses search response:', response);
      
      const allSynapses = response.success ? (response.synapses || []) : [];
      
      console.log('Total synapses for node:', allSynapses.length);
      
      // Normalize the nodeId for case-insensitive UUID comparison
      const normalizedNodeId = nodeId.toLowerCase();
      
      // Filter for outgoing (node is source)
      const outgoing = allSynapses.filter(s => {
        const sourceId = (s.sourcenodeid || s.sourceNodeId || s.SourceNodeId || '').toLowerCase();
        return sourceId === normalizedNodeId;
      });
      
      // Filter for incoming (node is target)
      const incoming = allSynapses.filter(s => {
        const targetId = (s.targetnodeid || s.targetNodeId || s.TargetNodeId || '').toLowerCase();
        return targetId === normalizedNodeId;
      });
      
      console.log('Outgoing synapses:', outgoing.length, 'Incoming synapses:', incoming.length);
      
      setConnectedSynapses({ incoming, outgoing });
    } catch (err) {
      console.error('Error loading connected synapses:', err);
      setConnectedSynapses({ incoming: [], outgoing: [] });
    }
  };

  useEffect(() => {
    if (currentEntity) {
      loadEntityData();
    }
  }, [currentEntity, loadEntityData]);

  // Navigation functions
  const navigateTo = useCallback((entity) => {
    if (currentEntity) {
      setHistory(prev => [...prev, currentEntity]);
    }
    setCurrentEntity(entity);
    setActiveTab(NODE_TABS.DETAILS);
    setIsEditing(false);
  }, [currentEntity]);

  const goBack = useCallback(() => {
    if (history.length > 0) {
      const newHistory = [...history];
      const previousEntity = newHistory.pop();
      setHistory(newHistory);
      setCurrentEntity(previousEntity);
      setActiveTab(NODE_TABS.DETAILS);
      setIsEditing(false);
    }
  }, [history]);

  // Build breadcrumb trail
  const breadcrumbs = useMemo(() => {
    const trail = [];
    
    history.forEach((entity, index) => {
      trail.push({
        id: entity.id,
        type: entity.type,
        label: entity.data?.name || entity.data?.nodeName || entity.data?.edgeName || `${entity.type} ${index + 1}`,
        onClick: () => {
          // Navigate back to this point
          const newHistory = history.slice(0, index);
          setHistory(newHistory);
          setCurrentEntity(entity);
          setActiveTab(NODE_TABS.DETAILS);
          setIsEditing(false);
        },
      });
    });
    
    // Add current entity
    if (currentEntity) {
      const currentLabel = currentEntity.type === ENTITY_TYPE.NODE
        ? (nodeData?.name || 'Node')
        : (synapseData?.edgeName || synapseData?.edgename || 'Connection');
      
      trail.push({
        id: currentEntity.id,
        type: currentEntity.type,
        label: currentLabel,
        isCurrent: true,
      });
    }
    
    return trail;
  }, [history, currentEntity, nodeData, synapseData]);

  // Handle node save
  const handleSaveNode = async () => {
    if (!nodeData) return;
    
    setSaving(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      let attributes = null;
      if (nodeForm.attributesJson && nodeForm.attributesJson.trim()) {
        try {
          attributes = JSON.parse(nodeForm.attributesJson);
        } catch (e) {
          throw new Error('Invalid JSON in attributes');
        }
      }
      
      const payload = {
        name: nodeForm.name.trim(),
        type: nodeForm.type.trim(),
        description: nodeForm.description.trim(),
      };
      
      if (attributes !== null) {
        payload.attributes = attributes;
      }
      
      const response = await apiService.updateNode(currentEntity.id, payload);
      
      if (response.success) {
        setSuccessMessage('Node saved successfully');
        setNodeData(response.node || { ...nodeData, ...payload });
        setIsEditing(false);
        
        if (onEntityUpdated) {
          onEntityUpdated();
        }
        
        // Clear success message after delay
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        throw new Error(response.message || 'Failed to save node');
      }
    } catch (err) {
      setError(err.message || 'Failed to save node');
    } finally {
      setSaving(false);
    }
  };

  // Handle synapse save
  const handleSaveSynapse = async () => {
    if (!synapseData) return;
    
    setSaving(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      const payload = {
        edgeName: synapseForm.edgeName.trim(),
        description: synapseForm.description.trim(),
        weight: parseFloat(synapseForm.weight) || 1.0,
        delayMs: parseFloat(synapseForm.delayMs) || 0,
      };
      
      const response = await apiService.updateSynapse(currentEntity.id, payload);
      
      if (response.success) {
        setSuccessMessage('Connection saved successfully');
        setSynapseData(response.synapse || { ...synapseData, ...payload });
        setIsEditing(false);
        
        if (onEntityUpdated) {
          onEntityUpdated();
        }
        
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        throw new Error(response.message || 'Failed to save synapse');
      }
    } catch (err) {
      setError(err.message || 'Failed to save synapse');
    } finally {
      setSaving(false);
    }
  };

  // Render node details
  const renderNodeDetails = () => {
    if (!nodeData) return null;
    
    if (isEditing) {
      return (
        <div className="entity-form">
          <div className="form-group">
            <label>Name</label>
            <input
              type="text"
              value={nodeForm.name}
              onChange={(e) => setNodeForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Node name"
            />
          </div>
          
          <div className="form-group">
            <label>Type</label>
            <input
              type="text"
              value={nodeForm.type}
              onChange={(e) => setNodeForm(prev => ({ ...prev, type: e.target.value }))}
              placeholder="Node type"
            />
          </div>
          
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={nodeForm.description}
              onChange={(e) => setNodeForm(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Description"
              rows={4}
            />
          </div>
          
          <div className="form-group">
            <label>Attributes (JSON)</label>
            <textarea
              value={nodeForm.attributesJson}
              onChange={(e) => setNodeForm(prev => ({ ...prev, attributesJson: e.target.value }))}
              placeholder='{"key": "value"}'
              rows={6}
              className="code-textarea"
            />
          </div>
          
          <div className="form-actions">
            <button 
              className="btn-primary"
              onClick={handleSaveNode}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            <button 
              className="btn-secondary"
              onClick={() => {
                setIsEditing(false);
                setNodeForm({
                  name: nodeData.name || '',
                  type: nodeData.type || '',
                  description: nodeData.description || '',
                  attributesJson: nodeData.attributes 
                    ? JSON.stringify(nodeData.attributes, null, 2) 
                    : '',
                });
              }}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </div>
      );
    }
    
    return (
      <div className="entity-details">
        <div className="detail-row">
          <span className="detail-label">Name</span>
          <span className="detail-value">{nodeData.name || 'N/A'}</span>
        </div>

        {Array.isArray(nodeData.aliases) && nodeData.aliases.length > 0 && (
          <div className="detail-row">
            <span className="detail-label">Aliases</span>
            <span className="detail-value">
              <span style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {nodeData.aliases.map((alias, idx) => (
                  <span
                    key={idx}
                    style={{
                      display: 'inline-block',
                      background: '#e8eaf6',
                      borderRadius: 12,
                      padding: '2px 10px',
                      fontSize: '0.88em',
                      color: '#3f51b5',
                    }}
                  >
                    {alias}
                  </span>
                ))}
              </span>
            </span>
          </div>
        )}
        
        <div className="detail-row">
          <span className="detail-label">Type</span>
          <span className="detail-value type-badge">{nodeData.type || 'N/A'}</span>
        </div>

        {nodeData.attributes?.domain_type && nodeData.attributes.domain_type !== 'general' && (
          <div className="detail-row">
            <span className="detail-label">Domain</span>
            <span className="detail-value" style={{
              display: 'inline-block',
              padding: '2px 8px',
              borderRadius: '10px',
              fontSize: '11px',
              fontWeight: 600,
              backgroundColor: '#e0e7ff',
              color: '#3730a3',
            }}>
              {nodeData.attributes.domain_type.replace(/_/g, ' ')}
            </span>
          </div>
        )}
        
        <div className="detail-row">
          <span className="detail-label">Description</span>
          <span className="detail-value description">{nodeData.description || 'No description'}</span>
        </div>
        
        <div className="detail-row">
          <span className="detail-label">Status</span>
          <span className={`detail-value status-badge status-${nodeData.status || 'active'}`}>
            {nodeData.status || 'active'}
          </span>
        </div>
        
        {nodeData.attributes && Object.keys(nodeData.attributes).length > 0 && (
          <div className="detail-row attributes-row">
            <span className="detail-label">Attributes</span>
            <pre className="detail-value code-block">
              {JSON.stringify(nodeData.attributes, null, 2)}
            </pre>
          </div>
        )}

        {processLinks.length > 0 && (
          <div className="detail-row" style={{ flexDirection: 'column', gap: 6 }}>
            <span className="detail-label">Process Links ({processLinks.length})</span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {processLinks.map((pl) => (
                <div
                  key={pl.GraphLinkId || pl.graph_link_id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    background: '#faf5ff', border: '1px solid #ddd6fe',
                    borderRadius: 8, padding: '6px 10px', fontSize: '0.82rem',
                  }}
                >
                  <span style={{ color: '#7c3aed', fontWeight: 600 }}>
                    {(pl.LinkDirection || pl.link_direction) === 'knowledge_to_process' ? '→' :
                     (pl.LinkDirection || pl.link_direction) === 'process_to_knowledge' ? '←' : '↔'}
                  </span>
                  <span>{pl.process_name || pl.ProcessNodeId || pl.process_node_id}</span>
                  {(pl.LinkPurpose || pl.link_purpose) && (
                    <span style={{ color: '#6b7280', fontSize: '0.75rem', marginLeft: 'auto' }}>
                      {(pl.LinkPurpose || pl.link_purpose).slice(0, 60)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        
        <div className="detail-row">
          <span className="detail-label">Last Updated</span>
          <span className="detail-value">
            {nodeData.lastUpdated 
              ? new Date(nodeData.lastUpdated).toLocaleString() 
              : 'N/A'}
          </span>
        </div>
        
        <div className="detail-row">
          <span className="detail-label">Owner</span>
          <span className="detail-value">{nodeData.owner || 'N/A'}</span>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
          <button 
            className="btn-edit"
            onClick={() => {
              if (onEditNode && nodeData) {
                onEditNode(nodeData);
              } else {
                setIsEditing(true);
              }
            }}
          >
            Edit Node
          </button>
          <button
            className="btn-link"
            disabled={reindexStatus === 'loading'}
            style={{
              background: 'none',
              border: 'none',
              color: reindexStatus === 'success' ? '#28a745'
                   : reindexStatus === 'error' ? '#dc3545'
                   : '#6c8ebf',
              cursor: reindexStatus === 'loading' ? 'wait' : 'pointer',
              fontSize: '0.85rem',
              padding: 0,
              textDecoration: 'underline',
            }}
            onClick={async () => {
              const nid = nodeData.nodeId || nodeData.id;
              if (!nid) return;
              setReindexStatus('loading');
              try {
                await apiService.reindexNode(nid);
                setReindexStatus('success');
              } catch {
                setReindexStatus('error');
              }
              setTimeout(() => setReindexStatus(null), 3000);
            }}
          >
            {reindexStatus === 'loading' ? 'Reindexing...'
             : reindexStatus === 'success' ? 'Reindexed'
             : reindexStatus === 'error' ? 'Failed'
             : 'Reindex'}
          </button>
        </div>
      </div>
    );
  };

  // Render synapse list (for incoming/outgoing tabs)
  const renderSynapseList = (synapses, direction) => {
    if (synapses.length === 0) {
      return (
        <div className="empty-state">
          <p>No {direction} connections found</p>
        </div>
      );
    }
    
    return (
      <div className="synapse-list">
        {synapses.map((synapse) => {
          const synapseId = synapse.synapseid || synapse.SynapseId || synapse.id;
          const edgeName = synapse.edgename || synapse.EdgeName || synapse.edgeName || 'Connection';
          const weight = synapse.weight || synapse.Weight || 1.0;
          
          // Determine the connected node
          const connectedNodeId = direction === 'outgoing'
            ? (synapse.targetnodeid || synapse.targetNodeId || synapse.TargetNodeId)
            : (synapse.sourcenodeid || synapse.sourceNodeId || synapse.SourceNodeId);
          const connectedNodeName = direction === 'outgoing'
            ? (synapse.targetnodename || synapse.targetNodeName || synapse.TargetNodeName || 'Unknown')
            : (synapse.sourcenodename || synapse.sourceNodeName || synapse.SourceNodeName || 'Unknown');
          
          return (
            <div key={synapseId} className="synapse-list-item">
              <div className="synapse-main">
                <button
                  className="synapse-link"
                  onClick={() => navigateTo({ 
                    type: ENTITY_TYPE.SYNAPSE, 
                    id: synapseId,
                    data: { edgeName }
                  })}
                >
                  <span className="edge-name">{edgeName}</span>
                  <span className="weight-badge">
                    <span className="weight-bar" style={{ width: `${Math.min(parseFloat(weight) * 100, 100)}%` }} />
                    <span className="weight-value">{parseFloat(weight).toFixed(2)}</span>
                  </span>
                </button>
              </div>
              
              <div className="synapse-meta">
                <span className="direction-indicator">
                  {direction === 'outgoing' ? '→' : '←'}
                </span>
                <button
                  className="node-link"
                  onClick={() => navigateTo({ 
                    type: ENTITY_TYPE.NODE, 
                    id: connectedNodeId,
                    data: { name: connectedNodeName }
                  })}
                >
                  {connectedNodeName}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Render synapse details
  const renderSynapseDetails = () => {
    if (!synapseData) return null;
    
    const sourceNodeId = synapseData.sourcenodeid || synapseData.sourceNodeId || synapseData.SourceNodeId;
    const sourceNodeName = synapseData.sourcenodename || synapseData.sourceNodeName || synapseData.SourceNodeName || 'Unknown';
    const targetNodeId = synapseData.targetnodeid || synapseData.targetNodeId || synapseData.TargetNodeId;
    const targetNodeName = synapseData.targetnodename || synapseData.targetNodeName || synapseData.TargetNodeName || 'Unknown';
    
    if (isEditing) {
      return (
        <div className="entity-form">
          <div className="form-group">
            <label>Edge Name</label>
            <input
              type="text"
              value={synapseForm.edgeName}
              onChange={(e) => setSynapseForm(prev => ({ ...prev, edgeName: e.target.value }))}
              placeholder="Edge name/type"
            />
          </div>
          
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={synapseForm.description}
              onChange={(e) => setSynapseForm(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Description"
              rows={4}
            />
          </div>
          
          <div className="form-row">
            <div className="form-group half">
              <label>Weight</label>
              <input
                type="number"
                step="0.01"
                value={synapseForm.weight}
                onChange={(e) => setSynapseForm(prev => ({ ...prev, weight: e.target.value }))}
              />
            </div>
            
            <div className="form-group half">
              <label>Delay (ms)</label>
              <input
                type="number"
                step="1"
                value={synapseForm.delayMs}
                onChange={(e) => setSynapseForm(prev => ({ ...prev, delayMs: e.target.value }))}
              />
            </div>
          </div>
          
          <div className="form-actions">
            <button 
              className="btn-primary"
              onClick={handleSaveSynapse}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            <button 
              className="btn-secondary"
              onClick={() => {
                setIsEditing(false);
                setSynapseForm({
                  edgeName: synapseData.edgeName || synapseData.edgename || '',
                  description: synapseData.description || '',
                  weight: synapseData.weight || 1.0,
                  delayMs: synapseData.delayMs || synapseData.delayms || 0,
                });
              }}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </div>
      );
    }
    
    return (
      <div className="entity-details synapse-details">
        <div className="connection-visualization">
          <button
            className="connection-node source"
            onClick={() => navigateTo({ 
              type: ENTITY_TYPE.NODE, 
              id: sourceNodeId,
              data: { name: sourceNodeName }
            })}
          >
            <span className="node-icon">●</span>
            <span className="node-name">{sourceNodeName}</span>
          </button>
          
          <div className="connection-edge">
            <span className="edge-line"></span>
            <span className="edge-label">
              {synapseData.edgeName || synapseData.edgename || 'connects to'}
            </span>
            <span className="edge-arrow">→</span>
          </div>
          
          <button
            className="connection-node target"
            onClick={() => navigateTo({ 
              type: ENTITY_TYPE.NODE, 
              id: targetNodeId,
              data: { name: targetNodeName }
            })}
          >
            <span className="node-icon">●</span>
            <span className="node-name">{targetNodeName}</span>
          </button>
        </div>
        
        <div className="detail-section">
          <h4>Synapse Properties</h4>
          
          <div className="detail-row">
            <span className="detail-label">Edge Name</span>
            <span className="detail-value">{synapseData.edgeName || synapseData.edgename || 'N/A'}</span>
          </div>
          
          <div className="detail-row">
            <span className="detail-label">Description</span>
            <span className="detail-value description">{synapseData.description || 'No description'}</span>
          </div>
          
          <div className="detail-row">
            <span className="detail-label">Weight</span>
            <span className="detail-value">{parseFloat(synapseData.weight || 1.0).toFixed(3)}</span>
          </div>
          
          <div className="detail-row">
            <span className="detail-label">Delay</span>
            <span className="detail-value">{synapseData.delayMs || synapseData.delayms || 0} ms</span>
          </div>
          
          <div className="detail-row">
            <span className="detail-label">Created</span>
            <span className="detail-value">
              {synapseData.createDate || synapseData.createdate
                ? new Date(synapseData.createDate || synapseData.createdate).toLocaleString() 
                : 'N/A'}
            </span>
          </div>
        </div>
        
        <button 
          className="btn-edit"
          onClick={() => {
            if (onEditSynapse && synapseData) {
              // Use full modal edit experience
              onEditSynapse(synapseData);
            } else {
              // Fallback to inline editing
              setIsEditing(true);
            }
          }}
        >
          Edit Connection
        </button>
      </div>
    );
  };

  // Render main content based on entity type
  const renderContent = () => {
    if (loading) {
      return (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      );
    }
    
    if (currentEntity?.type === ENTITY_TYPE.NODE) {
      return (
        <>
          <div className="entity-tabs">
            <button
              className={`tab ${activeTab === NODE_TABS.DETAILS ? 'active' : ''}`}
              onClick={() => setActiveTab(NODE_TABS.DETAILS)}
            >
              Details
            </button>
            <button
              className={`tab ${activeTab === NODE_TABS.OUTGOING ? 'active' : ''}`}
              onClick={() => setActiveTab(NODE_TABS.OUTGOING)}
            >
              Outgoing ({connectedSynapses.outgoing.length})
            </button>
            <button
              className={`tab ${activeTab === NODE_TABS.INCOMING ? 'active' : ''}`}
              onClick={() => setActiveTab(NODE_TABS.INCOMING)}
            >
              Incoming ({connectedSynapses.incoming.length})
            </button>
            <button
              className={`tab ${activeTab === NODE_TABS.NETWORK ? 'active' : ''}`}
              onClick={() => setActiveTab(NODE_TABS.NETWORK)}
            >
              Network
            </button>
          </div>
          
          <div className="entity-content">
            {activeTab === NODE_TABS.DETAILS && renderNodeDetails()}
            {activeTab === NODE_TABS.OUTGOING && renderSynapseList(connectedSynapses.outgoing, 'outgoing')}
            {activeTab === NODE_TABS.INCOMING && renderSynapseList(connectedSynapses.incoming, 'incoming')}
            {activeTab === NODE_TABS.NETWORK && (
              <NodeNeighborhoodGraph
                nodeId={currentEntity?.id}
                graphId={graphId}
                onNodeClick={(nId) => navigateTo({ id: nId, type: ENTITY_TYPE.NODE })}
              />
            )}
          </div>
        </>
      );
    }
    
    if (currentEntity?.type === ENTITY_TYPE.SYNAPSE) {
      return (
        <div className="entity-content">
          {renderSynapseDetails()}
        </div>
      );
    }
    
    return (
      <div className="empty-state">
        <p>Select an entity to view details</p>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="graph-entity-explorer-overlay" onClick={onClose}>
      <div className="graph-entity-explorer" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="explorer-header">
          <div className="header-nav">
            {history.length > 0 && (
              <button className="back-button" onClick={goBack} title="Go back">
                ← Back
              </button>
            )}
            
            <div className="breadcrumbs">
              {breadcrumbs.map((crumb, index) => (
                <span key={crumb.id} className="breadcrumb-item">
                  {index > 0 && <span className="breadcrumb-separator">/</span>}
                  {crumb.isCurrent ? (
                    <span className="breadcrumb-current">
                      <span className={`entity-type-icon ${crumb.type}`}>
                        {crumb.type === ENTITY_TYPE.NODE ? '○' : '—'}
                      </span>
                      {crumb.label}
                    </span>
                  ) : (
                    <button className="breadcrumb-link" onClick={crumb.onClick}>
                      <span className={`entity-type-icon ${crumb.type}`}>
                        {crumb.type === ENTITY_TYPE.NODE ? '○' : '—'}
                      </span>
                      {crumb.label}
                    </button>
                  )}
                </span>
              ))}
            </div>
          </div>
          
          <button className="close-button" onClick={onClose} title="Close">
            ×
          </button>
        </div>
        
        {/* Messages */}
        {error && (
          <div className="message error-message">
            {error}
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}
        {successMessage && (
          <div className="message success-message">
            {successMessage}
          </div>
        )}
        
        {/* Content */}
        <div className="explorer-body">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}

export default GraphEntityExplorer;
